import { useState, useEffect } from 'react'
import { Save, RefreshCw, Database, Bell, Shield, Key, Server, AlertCircle } from 'lucide-react'
import { adminAPI } from '../services/api'

const Settings = () => {
  const [settings, setSettings] = useState({
    general: {
      siteName: 'EuroMix',
      siteDescription: 'Інтернет-магазин товарів',
      timezone: 'Europe/Kiev',
      language: 'uk'
    },
    notifications: {
      emailEnabled: true,
      pushEnabled: true,
      smsEnabled: false,
      emailTemplate: 'default'
    },
    integration: {
      syncEnabled: true,
      syncInterval: '30',
      apiEndpoint: 'https://api.1c.com/sync',
      apiKey: '••••••••••••••••'
    },
    security: {
      sessionTimeout: '60',
      passwordMinLength: '8',
      twoFactorEnabled: false,
      loginAttempts: '5'
    }
  })

  const [activeTab, setActiveTab] = useState('general')
  const [saving, setSaving] = useState(false)
  const [testingConnection, setTestingConnection] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState(null)

  const tabs = [
    { id: 'general', name: 'Загальні', icon: Database },
    { id: 'notifications', name: 'Повідомлення', icon: Bell },
    { id: 'integration', name: 'Інтеграція', icon: RefreshCw },
    { id: 'security', name: 'Безпека', icon: Shield }
  ]

  useEffect(() => {
    // Завантажуємо налаштування при ініціалізації
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      // В реальному проекті тут буде API для завантаження налаштувань
      // const response = await adminAPI.getSettings()
      // setSettings(response.data.settings)
    } catch (error) {
      console.error('Error loading settings:', error)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // В реальному проекті тут буде API для збереження налаштувань
      // await adminAPI.updateSettings(settings)
      
      // Симуляція збереження
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Показуємо повідомлення про успішне збереження
      alert('Налаштування успішно збережено!')
    } catch (error) {
      console.error('Error saving settings:', error)
      alert('Помилка при збереженні налаштувань')
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    setTestingConnection(true)
    setConnectionStatus(null)
    
    try {
      // Тестуємо з'єднання з API статистики
      const response = await adminAPI.getStats()
      
      if (response.data.success) {
        setConnectionStatus('success')
      } else {
        setConnectionStatus('error')
      }
    } catch (error) {
      console.error('Connection test failed:', error)
      setConnectionStatus('error')
    } finally {
      setTestingConnection(false)
    }
  }

  const handleChange = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }))
  }

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Назва сайту
        </label>
        <input
          type="text"
          className="input"
          value={settings.general.siteName}
          onChange={(e) => handleChange('general', 'siteName', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Опис сайту
        </label>
        <textarea
          rows={3}
          className="input"
          value={settings.general.siteDescription}
          onChange={(e) => handleChange('general', 'siteDescription', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Часовий пояс
          </label>
          <select
            className="input"
            value={settings.general.timezone}
            onChange={(e) => handleChange('general', 'timezone', e.target.value)}
          >
            <option value="Europe/Kiev">Київ (UTC+2)</option>
            <option value="Europe/London">Лондон (UTC+0)</option>
            <option value="America/New_York">Нью-Йорк (UTC-5)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Мова
          </label>
          <select
            className="input"
            value={settings.general.language}
            onChange={(e) => handleChange('general', 'language', e.target.value)}
          >
            <option value="uk">Українська</option>
            <option value="en">English</option>
            <option value="ru">Русский</option>
          </select>
        </div>
      </div>
    </div>
  )

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">Email повідомлення</h4>
            <p className="text-sm text-gray-600">Відправляти повідомлення на email</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.notifications.emailEnabled}
              onChange={(e) => handleChange('notifications', 'emailEnabled', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">Push повідомлення</h4>
            <p className="text-sm text-gray-600">Відправляти push повідомлення через FCM</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.notifications.pushEnabled}
              onChange={(e) => handleChange('notifications', 'pushEnabled', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">SMS повідомлення</h4>
            <p className="text-sm text-gray-600">Відправляти SMS повідомлення</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.notifications.smsEnabled}
              onChange={(e) => handleChange('notifications', 'smsEnabled', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Шаблон email
        </label>
        <select
          className="input"
          value={settings.notifications.emailTemplate}
          onChange={(e) => handleChange('notifications', 'emailTemplate', e.target.value)}
        >
          <option value="default">Стандартний</option>
          <option value="modern">Сучасний</option>
          <option value="minimal">Мінімальний</option>
        </select>
      </div>
    </div>
  )

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Синхронізація з 1С</h4>
          <p className="text-sm text-gray-600">Автоматична синхронізація даних користувачів</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={settings.integration.syncEnabled}
            onChange={(e) => handleChange('integration', 'syncEnabled', e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Інтервал синхронізації (хвилини)
        </label>
        <input
          type="number"
          className="input"
          value={settings.integration.syncInterval}
          onChange={(e) => handleChange('integration', 'syncInterval', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          API Endpoint для 1С
        </label>
        <input
          type="url"
          className="input"
          value={settings.integration.apiEndpoint}
          onChange={(e) => handleChange('integration', 'apiEndpoint', e.target.value)}
          placeholder="https://api.1c.com/sync"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          API ключ для синхронізації
        </label>
        <div className="relative">
          <input
            type="password"
            className="input pr-10"
            value={settings.integration.apiKey}
            onChange={(e) => handleChange('integration', 'apiKey', e.target.value)}
            placeholder="Введіть API ключ"
          />
          <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Ключ для доступу до API синхронізації з 1С
        </p>
      </div>

      <div className={`border rounded-lg p-4 ${
        connectionStatus === 'success' 
          ? 'bg-green-50 border-green-200' 
          : connectionStatus === 'error'
          ? 'bg-red-50 border-red-200'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-center">
          {connectionStatus === 'success' ? (
            <Server className="h-5 w-5 text-green-600 mr-2" />
          ) : connectionStatus === 'error' ? (
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          ) : (
            <RefreshCw className="h-5 w-5 text-blue-600 mr-2" />
          )}
          <div>
            <h4 className={`font-medium ${
              connectionStatus === 'success' 
                ? 'text-green-900' 
                : connectionStatus === 'error'
                ? 'text-red-900'
                : 'text-blue-900'
            }`}>
              {connectionStatus === 'success' 
                ? 'З\'єднання успішне' 
                : connectionStatus === 'error'
                ? 'Помилка з\'єднання'
                : 'Тест з\'єднання'
              }
            </h4>
            <p className={`text-sm ${
              connectionStatus === 'success' 
                ? 'text-green-700' 
                : connectionStatus === 'error'
                ? 'text-red-700'
                : 'text-blue-700'
            }`}>
              {connectionStatus === 'success' 
                ? 'API доступний та працює коректно' 
                : connectionStatus === 'error'
                ? 'Не вдалося підключитися до API'
                : 'Перевірити підключення до API'
              }
            </p>
          </div>
        </div>
        <button 
          onClick={handleTestConnection}
          disabled={testingConnection}
          className="btn btn-secondary mt-3"
        >
          {testingConnection ? 'Тестування...' : 'Тестувати з\'єднання'}
        </button>
      </div>
    </div>
  )

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Тайм-аут сесії (хвилини)
        </label>
        <input
          type="number"
          className="input"
          value={settings.security.sessionTimeout}
          onChange={(e) => handleChange('security', 'sessionTimeout', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Мінімальна довжина пароля
        </label>
        <input
          type="number"
          className="input"
          value={settings.security.passwordMinLength}
          onChange={(e) => handleChange('security', 'passwordMinLength', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Максимальна кількість спроб входу
        </label>
        <input
          type="number"
          className="input"
          value={settings.security.loginAttempts}
          onChange={(e) => handleChange('security', 'loginAttempts', e.target.value)}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Двофакторна автентифікація</h4>
          <p className="text-sm text-gray-600">Додатковий рівень безпеки для адміністраторів</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={settings.security.twoFactorEnabled}
            onChange={(e) => handleChange('security', 'twoFactorEnabled', e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <Shield className="h-5 w-5 text-yellow-600 mr-2" />
          <div>
            <h4 className="font-medium text-yellow-900">Рекомендації з безпеки</h4>
            <p className="text-sm text-yellow-700">
              Регулярно оновлюйте паролі та використовуйте двофакторну автентифікацію
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings()
      case 'notifications':
        return renderNotificationSettings()
      case 'integration':
        return renderIntegrationSettings()
      case 'security':
        return renderSecuritySettings()
      default:
        return renderGeneralSettings()
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Налаштування</h1>
          <p className="text-gray-600">Управління налаштуваннями системи</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={20} />
          <span>{saving ? 'Збереження...' : 'Зберегти'}</span>
        </button>
      </div>

      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  )
}

export default Settings

